import { useQuery } from '@tanstack/react-query';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { approvalsGetApprovalOptions } from '@column/api';
import { Button, Chip, Icon, Toggle } from '@column/column-ui-kit';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { buildAchEvents, buildAchReturnEvents } from '~/app/pages/Transfers/_utils/ach';
import {
  isApprovalCandidateStatus,
  isApprovalHold,
  submitApprovalHold,
} from '~/app/pages/Transfers/_utils/transferReview';
import { ROUTE } from '~/app/routes';
import { CopyInput, Details, Loading, RenderFields } from '~/components';
import { useAccountNumber, useBankAccount, useCounterparty, useEntity } from '~/hooks';
import { useAchBatch } from '~/hooks/useTransferBatches';
import { useTransfer } from '~/hooks/useTransfers';
import { useOptimisticTransferUpdate } from '~/lib/api';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { BatchTransfersSheet, TransferHeader } from '~/lib/transfers';
import { AchRepository, AchReturn, AchTransfer, SimulateRepository, TransferEntryType } from '~/repositories';
import { convertTransferAmount } from '~/repositories/Transfer';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Divider, Inner } from '~/styles';
import { AchAchTransferStatus, ReviewTransferRequest } from '~/typings/enum';
import { formatDateTime } from '~/util';

import { AchReturnModal } from './_components/AchReturnModal';
import { InfoForm } from './_components/Layout';
import { Timeline } from './_components/Timeline';
import { TransferReview } from './_components/TransferReview';
import { EntryClassCodeDefinitions, IdempotencyKeyDefinitions } from './TypeContent/ach';

interface Params {
  id: string;
}

export const PageTransfersAch: FC = () => {
  const { id } = useParams<keyof Params>() as Params;
  const { isAuthorized, isSandbox } = useSessionStore();
  const navigate = useNavigate();
  const { addDangerNotification, addSuccessNotification } = useNotificationStore();
  const [isOpen, setIsOpen] = useState(false);
  const [achReturn, setAchReturn] = useState<DeepPartial<AchReturn>>();
  const achBatchesEnabled = useFeatureFlag(FeatureFlag.EnableAchTransferBatches);

  const {
    response: achBatch,
    isLoading: isAchBatchLoading,
    createRequest: fetchAchBatch,
  } = useAchBatch({
    onError: (error) => {
      addDangerNotification({ content: error.message });
    },
  });

  const {
    response: bankAccount,
    isLoading: isBankAccountLoading,
    createRequest: fetchBankAccount,
  } = useBankAccount({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: accountNumber,
    isLoading: isAccountNumberLoading,
    createRequest: fetchAccountNumber,
  } = useAccountNumber({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: originalTransfer,
    isLoading: isTransferLoading,
    createRequest: fetchTransfer,
  } = useTransfer<AchTransfer>('ach')({
    initialParams: { id },
    onSuccess: async (response) => {
      if (!response.accountNumberId) return;
      fetchAccountNumber({ id: response.accountNumberId });

      try {
        if (response.bankAccountId) {
          const currentBankAccount = await fetchBankAccount({ id: response.bankAccountId });

          if (!currentBankAccount) {
            return;
          }

          if (isAuthorized({ permission: 'entities', permissionLevel: 'read' })) {
            fetchEntity({ id: currentBankAccount.owners?.[0] });
            setIsLoadingEntity(true);
          }
        }
      } catch (error) {
        addDangerNotification({ content: (error as Error).message });
      }

      if (response?.counterpartyId) {
        fetchCounterparty({ id: response.counterpartyId });
      }
    },

    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: counterparty,
    isLoading: isCounterpartyLoading,
    createRequest: fetchCounterparty,
  } = useCounterparty({
    initialParams: originalTransfer?.counterpartyId ? { id: originalTransfer.counterpartyId } : undefined,
    onError: (error) => addDangerNotification({ content: error.message }),
  });

  const [isLoadingEntity, setIsLoadingEntity] = useState(false);
  const { response: entity, createRequest: fetchEntity } = useEntity({
    onSuccess: () => setIsLoadingEntity(false),
    onError: (error) => {
      setIsLoadingEntity(false);
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const [isReviewLoading, setReviewLoading] = useState(false);
  const optimisticallyUpdateTransfer = useOptimisticTransferUpdate();
  const transfer = originalTransfer;
  const isApprovalCandidate = isApprovalCandidateStatus(transfer?.status);
  const { data: approvalState } = useQuery({
    ...approvalsGetApprovalOptions({ path: { object_id: id } }),
    enabled: isApprovalCandidate,
  });
  const isPendingApproval = isApprovalCandidate && approvalState?.status === 'pending';
  const isMarkedApprovalHold = isPendingApproval && isApprovalHold(transfer?.status);
  const reviewTransfer = useCallback(
    async (req: ReviewTransferRequest) => {
      setReviewLoading(true);
      try {
        const updatedTransfer = await submitApprovalHold({
          id,
          request: req,
          transferType: transfer?.type === 'DEBIT' ? 'ach_debit' : 'ach_credit',
        });
        optimisticallyUpdateTransfer({ id: updatedTransfer.id, status: updatedTransfer.status });
        await fetchTransfer({ id });
      } catch (err) {
        addDangerNotification({
          content: (err as Error)?.message ?? 'An error occurred while reviewing the transfer.',
        });
      } finally {
        setReviewLoading(false);
      }
    },
    [addDangerNotification, fetchTransfer, id, optimisticallyUpdateTransfer, transfer?.status, transfer?.type]
  );

  const formattedTransfer = useMemo(
    () => (transfer ? (transfer as unknown as Record<string, unknown>) : undefined),
    [transfer]
  );

  const formMethods = useForm<AchTransfer>();

  useEffect(() => {
    if (transfer) {
      formMethods.setValue('description', transfer.description || '-');
      formMethods.setValue('idempotencyKey', transfer.idempotencyKey || '-');
      formMethods.setValue('allowOverdraft', transfer.allowOverdraft || false);
      formMethods.setValue('type', transfer.type || undefined);
      formMethods.setValue('effectiveOn', transfer.effectiveOn || '-');
      formMethods.setValue('entryClassCode', transfer.entryClassCode || '-');
      formMethods.setValue('paymentRelatedInfo', transfer.paymentRelatedInfo || '-');
      formMethods.setValue('companyId', transfer.companyId || '-');
      formMethods.setValue('companyName', transfer.companyName || '-');
      formMethods.setValue('companyEntryDescription', transfer.companyEntryDescription || '-');
      formMethods.setValue('companyDiscretionaryData', transfer.companyDiscretionaryData || '-');
      formMethods.setValue('receiverName', transfer.receiverName || '-');
      formMethods.setValue('receiverId', transfer.receiverId || '-');
    }
  }, [transfer]);

  useEffect(() => {
    if (
      transfer?.status &&
      ['RETURNED', 'RETURN_CONTESTED', 'RETURN_DISHONORED', 'PENDING_RETURN'].includes(transfer.status)
    ) {
      AchRepository.getReturn(id)
        .then(setAchReturn)
        .catch(() => null);
    }
  }, [transfer?.status, id]);

  useEffect(() => {
    if (achBatchesEnabled && transfer?.achTransferBatchId) {
      fetchAchBatch({ id: transfer.achTransferBatchId }).catch(() => null);
    }
  }, [achBatchesEnabled, transfer?.achTransferBatchId]);

  const isDataLoading = useMemo(() => {
    return (
      (transfer?.accountNumberId && isAccountNumberLoading) ||
      isBankAccountLoading ||
      (transfer?.counterpartyId && isCounterpartyLoading) ||
      (achBatchesEnabled && transfer?.achTransferBatchId && isAchBatchLoading) ||
      !transfer ||
      isTransferLoading
    );
  }, [
    isAccountNumberLoading,
    isBankAccountLoading,
    isCounterpartyLoading,
    isAchBatchLoading,
    isTransferLoading,
    transfer,
    achBatchesEnabled,
  ]);

  const handleSettle = useCallback(() => {
    if (!transfer) return;
    SimulateRepository.settleACH({ achTransferId: transfer.id })
      .then(() => {
        fetchTransfer();
        addSuccessNotification({
          content: 'ACH transfer settled',
        });
      })
      .catch((error: any) => {
        addDangerNotification({
          content: error.message,
        });
      });
  }, [isSandbox, transfer]);

  const handleCancel = () => {
    AchRepository.cancel(id)
      .then(() => {
        fetchTransfer();
        addSuccessNotification({
          content: 'ACH transfer cancelled',
        });
      })
      .catch((error: any) => {
        addDangerNotification({
          content: error.message,
        });
      });
  };

  const onReturn = useCallback(() => {
    setIsOpen(true);
  }, []);

  const timelineEntries = useMemo(
    () =>
      transfer && accountNumber && bankAccount
        ? buildAchEvents({
            transfer,
            account: {
              accountNumber,
              bankAccount,
              entityType: entity ? (entity.type.toLowerCase() as 'person' | 'business') : undefined,
            },
            counterparty: counterparty || undefined,
            company: {
              companyName: transfer.companyName,
              companyId: transfer.companyId,
            },
            achBatch: achBatch?.id === transfer.achTransferBatchId ? achBatch : undefined,
          })
        : [],
    [transfer, accountNumber, bankAccount, entity, counterparty, transfer?.companyName, transfer?.companyId, achBatch]
  );

  const returnEntries = useMemo(
    () =>
      transfer && accountNumber && bankAccount
        ? buildAchReturnEvents({
            achReturn,
            transfer,
            account: {
              accountNumber,
              bankAccount,
              entityType: entity ? (entity.type.toLowerCase() as 'person' | 'business') : undefined,
            },
            counterparty: counterparty || undefined,
            company: {
              companyName: transfer.companyName,
              companyId: transfer.companyId,
            },
          })
        : [],
    [achReturn, transfer, accountNumber, bankAccount, entity, counterparty, transfer?.companyName, transfer?.companyId]
  );

  const transferEntryType: TransferEntryType = transfer?.type === 'CREDIT' ? 'ach_credit' : 'ach_debit';

  return (
    <>
      <AchReturnModal transferId={transfer?.id ?? ''} isOpen={isOpen} onClose={() => setIsOpen(false)} />

      <PageActions>
        {isSandbox &&
          transfer?.status &&
          !['COMPLETED', 'REJECTED', 'SETTLED', 'CANCELED', 'RETURNED', 'PRE_REVIEW', 'HOLD'].includes(
            transfer.status
          ) && (
            <Button onClick={handleSettle} variant="primary" size="small" icon={<Icon.Sandbox />}>
              Settle
            </Button>
          )}

        {transfer?.status && ['INITIATED', 'PENDING_SUBMISSION'].includes(transfer.status) && (
          <Button variant="danger" size="small" onClick={handleCancel} icon={<Icon.Cross />}>
            Cancel
          </Button>
        )}

        {transfer?.returnedAt === null && transfer?.isIncoming && (
          <Button variant="danger" size="small" icon={<Icon.Return />} onClick={onReturn}>
            Return
          </Button>
        )}
      </PageActions>

      <Loading isLoading={isDataLoading} fullHeight>
        {transfer && (
          <TransferHeader
            amount={transfer.amount}
            createdAt={transfer.createdAt}
            isAdding={transfer?.type ? convertTransferAmount(transferEntryType, transfer.isIncoming) > 0 : undefined}
            direction={{
              type: transfer.status && transfer.isIncoming ? 'incoming' : 'outgoing',
            }}
            status={isMarkedApprovalHold ? AchAchTransferStatus.PRE_REVIEW : transfer.status!}
            transferId={transfer.id!}
            transferType={transferEntryType}
          />
        )}
        <TransferReview
          amount={transfer?.amount}
          bankAccountName={bankAccount?.displayName || bankAccount?.description || 'Unnamed Bank Account'}
          counterpartyName={counterparty?.name || counterparty?.description || 'Unnamed Counterparty'}
          createdByName={transfer?.createdBy?.name}
          description={transfer?.description}
          isDisabled={
            !isAuthorized({ permission: 'approveTransfers', permissionLevel: 'write', bankAccount }) ||
            !approvalState?.can_review
          }
          isLoading={isReviewLoading}
          isOpen={!!transfer && isPendingApproval}
          onSubmit={reviewTransfer}
        />
        {bankAccount && accountNumber && transfer && !isLoadingEntity && (
          <Timeline entries={timelineEntries} returnEntries={returnEntries} />
        )}

        <FormProvider {...formMethods}>
          <InfoForm autoComplete="off">
            {RenderFields<AchTransfer>({
              sections: [
                {
                  headline: 'Information',
                  fields: [
                    {
                      id: 'description',
                      label: 'Description',
                      children: ({ value }) => {
                        return <CopyInput value={String(value) || '-'} />;
                      },
                    },
                    {
                      id: 'idempotencyKey',
                      label: 'Idempotency Key',
                      tooltip: {
                        headline: 'Idempotency Keys',
                        content: IdempotencyKeyDefinitions,
                      },
                      children: ({ value }) => {
                        return <CopyInput value={String(value) || ''} isMonospaced />;
                      },
                    },
                    {
                      id: 'allowOverdraft',
                      label: 'Allow Overdraft',
                      children: ({ value, onChange }) => {
                        const isChecked = typeof value === 'boolean' ? value : false;
                        return <Toggle isChecked={isChecked} onCheckedChange={onChange} isDisabled />;
                      },
                    },
                  ],
                },
                {
                  headline: 'ACH',
                  fields: [
                    {
                      id: 'type',
                      label: 'Type',
                      children: ({ value }) => {
                        return (
                          <Chip size="large">
                            {(String(value) || '-').charAt(0).toUpperCase() +
                              (String(value) || '-').slice(1).toLowerCase()}
                          </Chip>
                        );
                      },
                    },
                    {
                      id: 'effectiveOn',
                      label: 'Effective Date',
                      children: ({ value }) => {
                        return <CopyInput value={formatDateTime(new Date(value), { timeZoneFormat: 'utc' }) || '-'} />;
                      },
                    },
                    {
                      id: 'entryClassCode',
                      label: 'Entry Class Code',
                      tooltip: {
                        headline: 'Entry Class Code Definitions',
                        content: EntryClassCodeDefinitions,
                      },
                      children: ({ value }) => {
                        return <Chip size="large">{String(value) || '-'}</Chip>;
                      },
                    },
                    {
                      id: 'paymentRelatedInfo',
                      label: 'Payment Related Info',
                      children: ({ value }) => {
                        return <CopyInput value={String(value) || '-'} />;
                      },
                    },
                  ],
                },
                {
                  headline: 'Company',
                  fields: [
                    {
                      id: 'companyId',
                      label: 'Company ID',
                      children: ({ value }) => {
                        return <CopyInput value={String(value) || '-'} isMonospaced />;
                      },
                    },
                    {
                      id: 'companyName',
                      label: 'Company Name',
                      children: ({ value }) => {
                        return <CopyInput value={String(value) || '-'} />;
                      },
                    },
                    {
                      id: 'companyEntryDescription',
                      label: 'Company Entry Description',
                      children: ({ value }) => {
                        return <CopyInput value={String(value) || '-'} />;
                      },
                    },
                    {
                      id: 'companyDiscretionaryData',
                      label: 'Company Discretionary Data',
                      children: ({ value }) => {
                        return <CopyInput value={String(value) || '-'} />;
                      },
                    },
                  ],
                },
                {
                  headline: 'Receiver',
                  fields: [
                    {
                      id: 'receiverName',
                      label: 'Receiver Name',
                      children: ({ value }) => {
                        return <CopyInput value={String(value) || '-'} />;
                      },
                    },
                    {
                      id: 'receiverId',
                      label: 'Receiver ID',
                      children: ({ value }) => {
                        return <CopyInput value={String(value) || '-'} isMonospaced />;
                      },
                    },
                  ],
                },
              ],
            })}
            <Divider />
            <Inner px={0} pt={24}>
              <Details
                label="Transfer Details"
                formattedData={formattedTransfer}
                rawData={transfer as Record<string, unknown> | undefined}
              />
            </Inner>
          </InfoForm>
        </FormProvider>
      </Loading>
      <BatchTransfersSheet />
    </>
  );
};
